import React, { useState } from "react";
import { Button, CircularProgress, Paper } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { FullScreenDialog } from "../FullScreenDialog";
import { Form, Formik } from "formik";
import { FINAL_RECOMENDATIONS, SERVICES, WITH_ESTIMATE_TIMELINE } from "./constants";
import { SubmitButton } from "../Buttons/SubmitButton";
import { ToggleButtonGroup } from "../Forms/fields/ToogleButtonGroup";
import { useQuery } from "@apollo/client";
import { GET_JOB_FOR_INSPECTION } from "../../graphql/queries/getJob";
import { Query, QueryGetInvoiceArgs, QueryGetJobArgs } from "../../generated/nest-graphql";
import { checkListSchema } from "./validation";
import { PostJobCheckListFormValues } from "./PostJobCheckListDialog";
import { DateInput } from "../Forms/fields/DateInput";
import { TextField } from "../Forms/fields/TextField";
import { CheckboxesField } from "../Forms/fields/CheckboxesField";
import { TextAreaField } from "../Forms/fields/TextAreaField";
import { useAiAxiosClient } from "../../lib/aiAxiosClient";
import { GET_INVOICE } from "../../graphql/queries/getInvoice";
import { useAllParts } from "../../hooks/useAllParts";
import { PartsList } from "./PartsList";

export const PostJobCheckListForm: React.FC<{
  onSubmit: (values) => void;
  open: boolean;
  onClose: (values) => void;
  jobId: string;
  initialValues: PostJobCheckListFormValues;
  invoiceId?: string;
  proceedingWithRepairs?: boolean;
}> = ({ onSubmit, open, onClose, jobId, initialValues, proceedingWithRepairs, invoiceId }) => {
  const { data } = useQuery<Query, QueryGetJobArgs>(GET_JOB_FOR_INSPECTION, {
    fetchPolicy: "cache-first",
    variables: {
      id: jobId,
    },
    skip: !jobId,
  });
  const { data: invoiceData } = useQuery<Query, QueryGetInvoiceArgs>(GET_INVOICE, {
    variables: { id: invoiceId || "" },
    skip: !invoiceId,
    fetchPolicy: "no-cache",
  });
  const [isRewriting, setIsRewriting] = useState(false);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const aiAxiosClient = useAiAxiosClient();

  const handleAIRewrite = async (currentNotes: string, setFieldValue: (field: string, value: any) => void) => {
    if (!currentNotes || currentNotes.trim().length === 0) {
      setRewriteError("Please enter some notes before rewriting");
      return;
    }

    setIsRewriting(true);
    setRewriteError(null);

    try {
      const response = await aiAxiosClient.post("/technicians/rewrite-customer-notes", {
        note: currentNotes,
      });

      if (response.data && response.data.text) {
        setFieldValue("customerNotes", response.data.text);
      } else {
        setRewriteError("No rewritten text received from AI");
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
      setRewriteError("Failed to rewrite notes. Please try again.");
    } finally {
      setIsRewriting(false);
    }
  };

  const allParts = useAllParts(invoiceData?.getInvoice);

  return (
    <Formik<PostJobCheckListFormValues>
      validationSchema={checkListSchema(proceedingWithRepairs)}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnMount
      validateOnBlur
      enableReinitialize
    >
      {({ values, isValid, isSubmitting, setFieldValue, errors }) => {
        return (
          <FullScreenDialog
            title={"Inspection"}
            open={open}
            onClose={() => {
              onClose(values);
            }}
            headerPosition={"fixed"}
          >
            <Paper className="pt-20">
              <Form className="h-full">
                <Box className={"px-4 h-full"}>
                  {proceedingWithRepairs && (
                    <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                      <h2 className="text-lg mb-4">
                        Are there any outstanding brake repair needs for this vehicle (not including any repairs
                        completed today)
                      </h2>
                      <div className="flex flex-row">
                        <ToggleButtonGroup
                          name="needOutstandingRepair"
                          items={[
                            {
                              label: "Yes",
                              value: true,
                            },
                            {
                              label: "No",
                              value: false,
                            },
                          ]}
                          activeBackground="#f04e23"
                          background="#FFF2"
                        />
                      </div>
                    </div>
                  )}
                  {(!proceedingWithRepairs || values["needOutstandingRepair"]) && (
                    <>
                      <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                        <h2 className="text-lg mb-4">What was your final recommendation to the customer?</h2>
                        <div className="flex flex-row">
                          <ToggleButtonGroup
                            name="finalRecomendation"
                            items={proceedingWithRepairs ? FINAL_RECOMENDATIONS.slice(0, 2) : FINAL_RECOMENDATIONS}
                            activeBackground="#f04e23"
                            background="#FFF2"
                            itemsClassName="!text-[11px]"
                          />
                        </div>
                      </div>
                      {WITH_ESTIMATE_TIMELINE.includes(values["finalRecomendation"]) && (
                        <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                          <h2 className="text-lg mb-4">What is your Estimated Repair Timeline?</h2>
                          <DateInput name="estimatedTimeline" label="Date" />
                          <TextField
                            name="notes"
                            rows={2}
                            placeholder="Notes"
                            inputProps={{
                              className: "content-center text-center",
                            }}
                            multiline
                          />
                        </div>
                      )}
                      {values["finalRecomendation"] && !WITH_ESTIMATE_TIMELINE.includes(values["finalRecomendation"]) && (
                        <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                          <h2 className="text-lg mb-4"> Which services did you recommend?</h2>
                          <CheckboxesField
                            name="recommendedServices"
                            options={SERVICES}
                            values={values["recommendedServices"]}
                            onChange={(e) => {
                              const newValues = [...(values["recommendedServices"] ?? [])];
                              const actualValueIndex = newValues.findIndex((item) => item === e.target.value);
                              if (actualValueIndex >= 0) {
                                newValues.splice(actualValueIndex, 1);
                              } else {
                                newValues.push(e.target.value);
                              }
                              setFieldValue("recommendedServices", newValues);
                            }}
                          />
                          <TextField
                            name="notes"
                            rows={2}
                            placeholder="Notes"
                            inputProps={{
                              className: "content-center text-center",
                            }}
                            multiline
                          />
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                    <h2 className="text-lg mb-4">
                      Did you substitute or add any parts that were not on the original order?
                    </h2>
                    <PartsList parts={allParts} />
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-row">
                        <ToggleButtonGroup
                          name="usedOriginalParts"
                          items={[
                            {
                              label: "Yes",
                              value: false,
                            },
                            {
                              label: "No — only original parts were used",
                              value: true,
                            },
                          ]}
                          activeBackground="#f04e23"
                          background="#FFF2"
                        />
                      </div>
                      {values.usedOriginalParts === false && (
                        <TextField
                          name="usedOriginalPartsNotes"
                          label="Describe the different parts used"
                          rows={2}
                          multiline
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                    <h2 className="text-lg">Customer Notes</h2>
                    <p className="text-sm text-gray-400 mb-2">
                      These notes will be visible to the customer on their invoice
                    </p>
                    <TextAreaField label={""} name="customerNotes" placeholder="" />
                    <div className="flex flex-row items-center mt-2 gap-2">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleAIRewrite(values.customerNotes || "", setFieldValue)}
                        disabled={isRewriting || isSubmitting}
                        startIcon={isRewriting ? <CircularProgress size={16} /> : null}
                        style={{
                          borderColor: "#f04e23",
                          backgroundColor: "#f04e23",
                          color: "#fff",
                          textTransform: "none",
                          fontSize: "12px",
                        }}
                      >
                        ✨Tidy Up
                      </Button>
                      {rewriteError && <span className="text-red-500 text-xs">{rewriteError}</span>}
                    </div>
                  </div>
                  <SubmitButton isSubmitting={isSubmitting || isRewriting} isValid={isValid} label={"Submit"} />
                </Box>
              </Form>
            </Paper>
          </FullScreenDialog>
        );
      }}
    </Formik>
  );
};
